/**
 * @file noteUi.tsx
 * @brief Pieces the web Notes pages share, in the mobile app's look.
 *
 * @details
 * DES, 30-09-2026: Notes on the web must feel like Notes on the phone. The
 * dock button is the web form of `VZActionDock`'s; the glass itself is
 * `noteDockGlass` in noteFormat.ts. The rename and tags dialogs
 * stand in for the phone's long-press sheets. The play button is
 * `NotePlaybackButton`: a circle with a brand progress ring.
 *
 * @author DES
 * @date 30-09-2026
 */

import { forwardRef, useEffect, useRef, useState, type ButtonHTMLAttributes } from "react";
import { CirclePlay, CircleStop, FileText } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { cn } from "@/shared/theme/cn";

/** A round icon button inside a dock, with its spoken label as a tooltip.
 *  Forwards its ref and extra props so it can be a menu's trigger. */
export const DockIconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> & {
    label: string;
    onClick?: () => void;
    children: React.ReactNode;
    tone?: "default" | "brand" | "danger";
    active?: boolean;
  }
>(function DockIconButton(
  { label, onClick, children, tone = "default", disabled, active, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      {...rest}
      onClick={onClick ? () => onClick() : undefined}
      className={cn(
        "flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-secondary disabled:opacity-50",
        tone === "brand" && "text-brand",
        tone === "danger" && "text-destructive",
        tone === "default" && "text-foreground",
        active && "bg-secondary",
        className,
      )}
    >
      {children}
    </button>
  );
});

// ----------------------------------------------------------------------
// Rename — the phone's "Rename note" modal
// ----------------------------------------------------------------------
export function NoteRenameDialog({
  open,
  onOpenChange,
  initialTitle,
  saving,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTitle: string;
  saving?: boolean;
  onSave: (title: string) => void;
}) {
  const [value, setValue] = useState(initialTitle);
  useEffect(() => {
    if (open) setValue(initialTitle);
  }, [open, initialTitle]);
  const next = value.trim();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename note</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (next) onSave(next);
          }}
        >
          <label htmlFor="note-title" className="text-sm font-semibold">
            Note title
          </label>
          <div className="relative mt-2">
            <FileText className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              capitalizeFirst
              id="note-title"
              autoFocus
              value={value}
              maxLength={120}
              placeholder="Enter a title"
              onChange={(e) => setValue(e.target.value)}
              className="h-12 pl-10"
            />
          </div>
          <DialogFooter className="mt-5 gap-2">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!next || saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------
// Tags — the phone's `NoteTagsEditorModal`
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Playback — the phone's `NotePlaybackButton`: a circle, a brand ring.
// ----------------------------------------------------------------------
export function NotePlayButton({ src, className }: { src: string; className?: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const audio = new Audio(src);
    audio.preload = "metadata";
    audioRef.current = audio;
    const onTime = () => setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("pause", () => setPlaying(false));
    audio.addEventListener("play", () => setPlaying(true));
    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnd);
      audioRef.current = null;
    };
  }, [src]);

  // The phone's numbers (NotePlaybackButton.tsx): a 44px CirclePlay in a
  // 54px host; the progress ring (2.5 stroke) shows only while playing, and
  // playing swaps the glyph for CircleStop. (30-09-2026)
  const RING = 54;
  const STROKE = 2.5;
  const R = (RING - STROKE) / 2;
  const C = 2 * Math.PI * R;
  return (
    <button
      type="button"
      aria-label={playing ? "Stop playback" : "Play recording"}
      onClick={() => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) void a.play();
        else {
          a.pause();
          a.currentTime = 0;
          setProgress(0);
        }
      }}
      className={cn(
        "relative flex h-[54px] w-[54px] shrink-0 items-center justify-center",
        className,
      )}
    >
      {playing ? (
        <svg viewBox={`0 0 ${RING} ${RING}`} className="absolute inset-0 -rotate-90">
          <circle
            cx={RING / 2}
            cy={RING / 2}
            r={R}
            fill="none"
            strokeWidth={STROKE}
            className="stroke-app-quaternary"
          />
          <circle
            cx={RING / 2}
            cy={RING / 2}
            r={R}
            fill="none"
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={C * (1 - Math.max(0, Math.min(1, progress)))}
            className="stroke-brand transition-[stroke-dashoffset] duration-200"
          />
        </svg>
      ) : null}
      {playing ? (
        <CircleStop className="h-11 w-11 text-brand" />
      ) : (
        <CirclePlay className="h-11 w-11 text-brand" />
      )}
    </button>
  );
}
